import { __assign, __extends } from '../../_virtual/_tslib.js';
import React from 'react';
import classnames from 'classnames';
import useBreakpoint from '../../hooks/useBreakpoint.js';
import hash from 'object-hash';
import omit from 'lodash/omit';
import isUndefined from 'lodash/isUndefined';
import '../Loader/BubbleLoader/index.js';
import CircleLoader from '../Loader/CircleLoader/index.js';
import '../Loader/FullBlockLoader/index.js';
import '../Loader/FullPageLoader/index.js';
import '../Loader/GlobalLoader/index.js';
import '../Loader/SealLoader/index.js';
import textFieldStyles from './styles.css.js';

var ResponsiveTextField =
/** @class */
function (_super) {
  __extends(ResponsiveTextField, _super);

  function ResponsiveTextField(props) {
    var _this = _super.call(this, props) || this;

    _this.state = {
      // Autofocus will always exist via `defaultProps`
      hasFocus: _this.props.autoFocus
    };
    _this.handleInputBlur = _this.handleInputBlur.bind(_this);
    _this.handleInputChange = _this.handleInputChange.bind(_this);
    _this.handleInputFocus = _this.handleInputFocus.bind(_this);
    return _this;
  }
  /**
   * Generate consistent ID for this input based on the props received:
   *  - Used to ensure the label and input are properly associated.
   *  - Must be consistent across server and browser renders.
   *  - Only used if an explicit `id` prop is not passed.
   */


  ResponsiveTextField.prototype.generateId = function () {
    return 'text-field-' + hash(this.props, {
      ignoreUnknown: true
    }).substr(0, 8);
  };

  ResponsiveTextField.prototype.componentDidUpdate = function (prevProps) {
    var id = this.props.id || this.generateId(); // Controlled focus => uncontrolled focus isn't allowed

    if (!isUndefined(prevProps.focused) && isUndefined(this.props.focused)) {
      console.warn("TextField (" + id + ") is changing from controlled focus to uncontrolled focus. TextFields should not switch from controlled to uncontrolled (or vice versa).");
    } // Uncontrolled focus => controlled focus isn't allowed


    if (isUndefined(prevProps.focused) && !isUndefined(this.props.focused)) {
      console.warn("TextField (" + id + ") is changing from uncontrolled focus to controlled focus. TextFields should not switch from uncontrolled to controlled (or vice versa).");
    }
  };

  ResponsiveTextField.prototype.handleInputFocus = function (e) {
    // Only manage the input focus if the focus is not controlled by props
    if (isUndefined(this.props.focused)) {
      this.setState({
        hasFocus: true
      });
    }

    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  };

  ResponsiveTextField.prototype.handleInputBlur = function (e) {
    // Only manage the input focus if the focus is not controlled by props
    if (isUndefined(this.props.focused)) {
      this.setState({
        hasFocus: false
      });
    }

    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  };

  ResponsiveTextField.prototype.handleInputChange = function (e) {
    // Only manage the input value if the value is not controlled by props
    if (isUndefined(this.props.value)) {
      this.setState({
        hasValue: !!(e.target.value && e.target.value.length > 0)
      });
    }

    if (this.props.onChange) {
      this.props.onChange(e);
    }
  };

  ResponsiveTextField.prototype.render = function () {
    var _a, _b, _c;

    var _d = this.props,
        Icon = _d.icon,
        testId = _d.testId,
        className = _d.className,
        innerInputContainerClassName = _d.innerInputContainerClassName;
    var id = this.props.id || this.generateId();
    var hasValue = !!(this.props.value || this.props.defaultValue || this.state.hasValue || this.props.inputProps && this.props.inputProps.value);
    var hasFocus = !!(this.props.focused || this.state.hasFocus);
    var hasError = this.props.errorText !== undefined || this.props.error;
    var isLoading = !!this.props.loading;
    var isDisabled = !!this.props.disabled;
    var isLabelHidden = !!this.props.labelHidden;
    var hasUnit = !!this.props.unit;
    /**
     * This is a gross edge case until we can clean up the backward incompatabilities
     * Tracking here: https://dealdotcom.atlassian.net/browse/CURATE-9183
     */

    var hasPrependUnit = hasUnit && (this.props.unitPlacement === 'left' || this.props.unit === '$');
    return React.createElement("div", {
      className: classnames(textFieldStyles.field, textFieldStyles[this.props.size], (_a = {}, _a[textFieldStyles.hasFocus] = hasFocus, _a[textFieldStyles.hasValue] = hasValue, _a[textFieldStyles.hasError] = hasError, _a[textFieldStyles.isLoading] = isLoading, _a[textFieldStyles.isDisabled] = isDisabled, _a[textFieldStyles.isLabelHidden] = isLabelHidden, _a), className)
    }, React.createElement("div", {
      className: textFieldStyles.inputContainer
    }, this.props.label && !this.props.labelHidden && React.createElement("label", {
      id: 'label-' + id,
      htmlFor: id,
      className: textFieldStyles.label
    }, this.props.label), React.createElement("div", {
      className: classnames(textFieldStyles.innerInputContainer, innerInputContainerClassName)
    }, hasPrependUnit && React.createElement("div", {
      className: textFieldStyles.prependUnit
    }, this.props.unit), React.createElement("div", {
      className: textFieldStyles.fieldContainer
    }, Icon && React.createElement(Icon, {
      className: textFieldStyles.additionalIcon
    }), React.createElement("input", __assign({
      "data-testid": testId,
      className: classnames(textFieldStyles.input, this.props.inputProps && this.props.inputProps.className, (_b = {}, _b[textFieldStyles.withUnit] = hasUnit, _b[textFieldStyles.withIcon] = Icon, _b[textFieldStyles.withError] = hasError, _b)),
      disabled: this.props.disabled,
      defaultValue: this.props.defaultValue,
      id: id,
      name: this.props.name,
      onBlur: this.handleInputBlur,
      onChange: this.handleInputChange,
      onFocus: this.handleInputFocus,
      placeholder: this.props.placeholder || isLabelHidden && this.props.label || undefined,
      readOnly: this.props.readOnly,
      type: this.props.type,
      value: this.props.value
    }, omit(this.props.inputProps, 'className'), {
      ref: this.props.inputRef
    })), isLoading && React.createElement(CircleLoader, {
      color: "#087bcc",
      size: "16px",
      thickness: "2px",
      className: textFieldStyles.loadingIcon
    })), hasUnit && !hasPrependUnit && React.createElement("div", {
      className: classnames(textFieldStyles.unit, (_c = {}, _c[textFieldStyles.withError] = hasError, _c))
    }, this.props.unit))), this.props.errorText && React.createElement("div", {
      className: textFieldStyles.errorText
    }, this.props.errorText), this.props.successText && React.createElement("div", {
      className: textFieldStyles.successText
    }, this.props.successText), this.props.helpText && React.createElement("div", {
      className: textFieldStyles.helpText
    }, this.props.helpText));
  };

  ResponsiveTextField.defaultProps = {
    autoFocus: false
  };
  return ResponsiveTextField;
}(React.PureComponent);

var TextField = React.forwardRef(function (props, ref) {
  var isMobile = !useBreakpoint('sm');
  return React.createElement(ResponsiveTextField, __assign({
    inputRef: ref,
    isMobile: isMobile
  }, props));
});

export default TextField;
//# sourceMappingURL=index.js.map
